import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiXLight,
  PiQuestionLight,
  PiCaretDownLight,
  PiCaretRightLight,
  PiReceiptLight,
  PiScalesLight,
  PiCurrencyDollarLight,
  PiBankLight,
  PiArrowsLeftRightLight,
  PiUsersLight,
  PiChartPieLight,
} from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface FinanceHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  topics: HelpTopic[];
}

interface HelpTopic {
  name: string;
  explanation: string;
  howToUse: string;
  tips: string;
}

/* ------------------------------------------------------------------ */
/*  Help Content                                                       */
/* ------------------------------------------------------------------ */

const SECTIONS: HelpSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    icon: <PiChartPieLight className="w-4 h-4" />,
    description:
      'The Overview tab gives you a high-level snapshot of your organization\'s financial health across all categories.',
    topics: [
      {
        name: 'Summary cards',
        explanation:
          'The top cards show key totals like total invoiced, total expenses, available balance, and outstanding amounts.',
        howToUse: 'Use these cards for a quick financial health check without diving into individual tabs.',
        tips: 'Amounts are shown in your organization\'s default currency and update in real-time.',
      },
      {
        name: 'Quick navigation',
        explanation:
          'Each summary section links to its detailed tab so you can drill into any area that needs attention.',
        howToUse: 'Click on a section heading or a card to jump directly to the related detailed view.',
        tips: 'Start your day here to see what needs action, then navigate to specific tabs.',
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    icon: <PiReceiptLight className="w-4 h-4" />,
    description:
      'Create, manage, and track professional invoices for your clients. Supports multi-step creation with team member assignment and billing rate integration.',
    topics: [
      {
        name: 'Creating an invoice',
        explanation:
          'The invoice wizard walks you through 4 steps: Select Client & Matter, Add Line Items, Configure Adjustments (tax, discount, terms), and Preview before sending.',
        howToUse:
          'Click "New Invoice" in the header. Select a client (optional), add line items manually or from billing rates/expenses, adjust tax and terms, then create.',
        tips: 'You can assign team members to line items — their billing rate will auto-populate based on the configured rate hierarchy.',
      },
      {
        name: 'Team member rates',
        explanation:
          'When adding line items, you can assign a team member. The system resolves their applicable billing rate based on matter → client → user → org default priority.',
        howToUse:
          'Use the "Team Member" dropdown on line items or the "+ Team Member" button to add a person with their rate pre-filled.',
        tips: 'Set up billing rates first (in the Rates tab) so invoices can automatically pull the correct hourly rate per person.',
      },
      {
        name: 'Importing expenses',
        explanation:
          'Unbilled expenses linked to the selected client or matter can be imported directly as invoice line items.',
        howToUse: 'Click "Import Expenses" in the line items step, select the relevant expenses, and they\'ll be added as line items.',
        tips: 'Imported expenses are automatically marked as "invoiced" when the invoice is created.',
      },
      {
        name: 'Invoice statuses',
        explanation:
          'Invoices progress through: Draft → Sent → Paid (or Overdue / Void). Status updates automatically when payments are recorded.',
        howToUse: 'Click on any invoice row to view its full details, update status, or record a payment.',
        tips: 'Recurring invoices can be set up in the Adjustments step to auto-generate on a schedule.',
      },
    ],
  },
  {
    id: 'rates',
    title: 'Billing Rates',
    icon: <PiScalesLight className="w-4 h-4" />,
    description:
      'Configure billing rates at different scopes — organization-wide defaults, per-user, per-client, or per-matter. The most specific rate always takes priority.',
    topics: [
      {
        name: 'Rate types',
        explanation:
          'Supported types: Hourly (per-hour charge), Fixed Fee (flat amount for a service), Contingency (percentage-based), Retainer (recurring retainer), and Flat Rate.',
        howToUse: 'Click "New Rate" and select the rate type, then set the amount, description, and optional scope.',
        tips: 'Set an organization default hourly rate first — it acts as the fallback for any user or matter that doesn\'t have a specific rate.',
      },
      {
        name: 'Rate hierarchy',
        explanation:
          'When resolving which rate to apply, the system checks in order: Matter+User → Matter → Client+User → Client → User → Organization Default.',
        howToUse:
          'Create a broad organization default, then add specific rates for high-value clients or specialized team members as needed.',
        tips: 'Effective dates let you schedule rate changes in advance without disrupting current invoices.',
      },
      {
        name: 'Quick-add to invoices',
        explanation:
          'Billing rates appear as quick-add options in the invoice creation wizard, letting you populate line items with pre-configured services.',
        howToUse:
          'In the invoice wizard, click "Add from Rate" to see all configured rates and add them as line items with one click.',
        tips: 'Use descriptive names for your rates (e.g., "Senior Associate Hourly", "Document Review") so they\'re easy to identify during invoicing.',
      },
    ],
  },
  {
    id: 'expenses',
    title: 'Expenses',
    icon: <PiCurrencyDollarLight className="w-4 h-4" />,
    description:
      'Track costs and disbursements associated with your work. Expenses can be marked as billable and later imported into invoices.',
    topics: [
      {
        name: 'Expense categories',
        explanation:
          'Categories include Filing Fees, Court Costs, Travel, Expert Witness, Copying & Printing, Postage & Delivery, Research, Technology, and Other.',
        howToUse: 'Click "New Expense" and select a category, enter the amount and description, mark as billable if applicable.',
        tips: 'Billable expenses can be imported directly into client invoices to ensure cost recovery.',
      },
      {
        name: 'Expense workflow',
        explanation:
          'Expenses follow a workflow: Pending → Approved → Invoiced (or Written Off). Only pending expenses can be edited.',
        howToUse: 'Use the action buttons to approve, write off, or delete expenses. Approved expenses are eligible for invoicing.',
        tips: 'Use filters to quickly find pending expenses that need approval.',
      },
      {
        name: 'Summary metrics',
        explanation:
          'The summary cards show Total Expenses, Billable amount, and Non-Billable amount for a quick financial overview.',
        howToUse: 'Review summary cards at the top of the Expenses tab for an instant snapshot.',
        tips: 'A high non-billable ratio might indicate costs you should discuss with clients about cost recovery.',
      },
    ],
  },
  {
    id: 'payouts',
    title: 'Payouts & Distributions',
    icon: <PiBankLight className="w-4 h-4" />,
    description:
      'Manage money going out of your Stripe account — payouts to your bank and internal compensation distributions to team members.',
    topics: [
      {
        name: 'Payouts',
        explanation:
          'Payouts transfer money from your Stripe balance to your connected bank account. They can be automatic or manually initiated.',
        howToUse:
          'Click "Manual Payout" to initiate a transfer. The maximum amount is limited to your available Stripe balance.',
        tips: 'Sync payouts regularly to keep your local records aligned with Stripe.',
      },
      {
        name: 'Distributions',
        explanation:
          'Distributions track internal compensation — salaries, bonuses, matter fees, referral fees, and expense reimbursements to team members.',
        howToUse:
          'Click "New Distribution" in the Distributions section, select a team member from the dropdown, choose the type, and enter the amount.',
        tips: 'Distributions follow an approval workflow: Pending → Approved → Paid. Use this to maintain an audit trail.',
      },
      {
        name: 'Balance overview',
        explanation: 'The summary cards show Available for Payout, Total Paid Out, and Pending / In Transit amounts.',
        howToUse: 'Check the "Available for Payout" card before initiating manual payouts.',
        tips: 'Pending amounts are funds that Stripe is processing — they\'ll become available shortly.',
      },
    ],
  },
  {
    id: 'transactions',
    title: 'Transactions',
    icon: <PiArrowsLeftRightLight className="w-4 h-4" />,
    description:
      'View all balance transactions from your Stripe account — charges, refunds, payouts, fees, and adjustments.',
    topics: [
      {
        name: 'Transaction types',
        explanation:
          'Transactions include charges (incoming payments), refunds, payouts, Stripe fees, adjustments, and transfers.',
        howToUse: 'Use the type filter to narrow down to specific transaction categories.',
        tips: 'Transaction data syncs from Stripe — use "Sync" to pull the latest activity.',
      },
      {
        name: 'Summary metrics',
        explanation:
          'The summary cards show Gross Amount (total charges), Net Amount (after fees and refunds), and Total Fees.',
        howToUse: 'Use summary cards to quickly understand your revenue versus costs.',
        tips: 'The difference between Gross and Net represents Stripe processing fees + refunds.',
      },
    ],
  },
  {
    id: 'bank',
    title: 'Bank Account',
    icon: <PiBankLight className="w-4 h-4" />,
    description:
      'Connect and manage your Stripe Connect account for payment processing. View account status, capabilities, and balance.',
    topics: [
      {
        name: 'Account setup',
        explanation:
          'Your Stripe Connect account handles payment processing. It needs to be fully onboarded and verified before you can accept payments.',
        howToUse:
          'If not yet connected, click "Connect Stripe" to start the onboarding flow. Complete all required verification steps.',
        tips: 'Ensure your business details, bank account, and identity verification are complete to avoid payment delays.',
      },
      {
        name: 'Account capabilities',
        explanation: 'Capabilities like "card_payments" and "transfers" determine what your account can do.',
        howToUse: 'Review the capabilities section to ensure all needed features are active.',
        tips: 'If a capability shows as "pending", Stripe may need additional verification documents.',
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Collapsible Section                                                */
/* ------------------------------------------------------------------ */

const CollapsibleSection: React.FC<{ section: HelpSection }> = ({ section }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <span className="text-gray-500 dark:text-gray-400">{section.icon}</span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{section.title}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{section.description}</p>
        </div>
        <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 shrink-0">
          {section.topics.length} {section.topics.length === 1 ? 'topic' : 'topics'}
          {expanded ? <PiCaretDownLight className="w-3.5 h-3.5" /> : <PiCaretRightLight className="w-3.5 h-3.5" />}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
          {section.topics.map((topic, i) => (
            <div key={i} className="px-5 py-4">
              <div className="flex items-center gap-2 mb-1.5">
                <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100">{topic.name}</h4>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{topic.explanation}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-[#f5f5f5] dark:bg-gray-800/60 rounded-lg px-4 py-3">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">How to use</span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{topic.howToUse}</p>
                </div>
                <div className="bg-[#f5f5f5] dark:bg-gray-800/60 rounded-lg px-4 py-3">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Tips</span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{topic.tips}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Modal                                                         */
/* ------------------------------------------------------------------ */

const FinanceHelpModal: React.FC<FinanceHelpModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const totalTopics = SECTIONS.reduce((sum, s) => sum + s.topics.length, 0);

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[90vh] bg-[#f5f5f5] dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <PiQuestionLight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('Understanding your billing & finance')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('Learn how each section works and get tips for efficient financial management')} — {totalTopics} {t('topics')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <PiXLight className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-3">
          {SECTIONS.map((section) => (
            <CollapsibleSection key={section.id} section={section} />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <p className="text-[10px] text-gray-400 dark:text-gray-500">
            {t('Set up billing rates and connect your Stripe account to unlock the full billing workflow.')}
          </p>
          <Button variant="neutral" size="small" onClick={onClose}>
            {t('Close')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FinanceHelpModal;
